import React, { useEffect, useState } from "react";
import { api, unwrapArray } from "@/lib/api";
import { SearchSelect } from "@/components/ui/search-select";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import type { MasterLookupRecord, ProductVariantRecord, SampleRecord } from "@/types";

const field = "w-full h-10 rounded-lg border border-input bg-background px-3 text-sm focus:outline-hidden focus:ring-2 focus:ring-ring";

const EMPTY = {
  deal_id: "",
  deal_label: "",
  product_id: "",
  product_label: "",
  variant_id: "",
  product_name: "",
  sample_type: "",
  quantity: "",
  unit: "",
  details: "",
  needed_by: "",
};

interface Props {
  /** "new" to request a sample, a sample to edit it, null when closed */
  target: SampleRecord | "new" | null;
  /** Fixes the deal (on the deal page); without it the form asks which deal */
  dealId?: string;
  onClose: () => void;
  onSaved: () => void;
}

/** Request a sample, or edit one that has not been dispatched or decided */
export function SampleFormDialog({ target, dealId, onClose, onSaved }: Props) {
  const { success, error } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [types, setTypes] = useState<MasterLookupRecord[]>([]);
  const [units, setUnits] = useState<MasterLookupRecord[]>([]);
  const [variants, setVariants] = useState<ProductVariantRecord[]>([]);
  const [saving, setSaving] = useState(false);
  const open = !!target;
  const editing = target && target !== "new" ? target : null;

  useEffect(() => {
    if (!open) return;
    api.get("/api/MasterLookup/ByCategory/SAMPLE_TYPE").then((r) => setTypes(unwrapArray<MasterLookupRecord>(r))).catch(() => undefined);
    api.get("/api/MasterLookup/ByCategory/PRODUCT_UNIT").then((r) => setUnits(unwrapArray<MasterLookupRecord>(r))).catch(() => undefined);
  }, [open]);

  const loadVariants = (productId: string) => {
    setVariants([]);
    if (!productId) return;
    api
      .get(`/api/Product/Variants/${productId}`)
      .then((r) => setVariants(unwrapArray<ProductVariantRecord>(r).filter((v) => v.status !== false)))
      .catch(() => setVariants([]));
  };

  useEffect(() => {
    if (!target) return;
    if (target === "new") {
      setForm({ ...EMPTY, deal_id: dealId || "" });
      setVariants([]);
      return;
    }
    setForm({
      deal_id: target.deal_id,
      deal_label: target.deal?.name || "",
      product_id: target.product_id || "",
      product_label: target.product_name,
      variant_id: target.variant_id || "",
      product_name: target.product_name,
      sample_type: target.sample_type,
      quantity: target.quantity != null ? String(Number(target.quantity)) : "",
      unit: target.unit || "",
      details: target.details || "",
      needed_by: target.needed_by ? String(target.needed_by).slice(0, 10) : "",
    });
    loadVariants(target.product_id || "");
  }, [target, dealId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.deal_id) return error("Validation Error", "Choose the deal this sample is for");
    if (!form.sample_type) return error("Validation Error", "Choose the sample type");
    if (!form.product_id && !form.product_name.trim()) return error("Validation Error", "Choose a product or enter the article name");
    if (Number(form.quantity) > 0 && !form.unit) return error("Validation Error", "Choose the unit for the quantity");

    const payload = {
      deal_id: editing ? undefined : form.deal_id,
      product_id: form.product_id || null,
      variant_id: form.product_id ? form.variant_id || null : null,
      product_name: form.product_name.trim() || undefined,
      sample_type: form.sample_type,
      quantity: form.quantity === "" ? null : Number(form.quantity),
      unit: form.unit || null,
      details: form.details.trim() || null,
      needed_by: form.needed_by || null,
    };
    setSaving(true);
    try {
      if (editing) await api.put(`/api/Sample/Update/${editing.id}`, payload);
      else await api.post("/api/Sample/Insert", payload);
      success(editing ? "Sample updated" : "Sample requested", editing ? "Details saved" : "Track it from the Samples list or the deal page");
      onSaved();
      onClose();
    } catch (err: any) {
      error("Save Failed", err.message || "Could not save the sample");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="sm:max-w-xl md:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit sample ${editing.sample_number}` : "Request a sample"}</DialogTitle>
          <DialogDescription>Samples go to the buyer for approval before the order. Approval moves the deal on from Sampling.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 py-2 text-sm">
          {!dealId && !editing && (
            <div className="space-y-1.5">
              <Label>Deal *</Label>
              <SearchSelect
                endpoint="/api/Deal/List"
                extraParams={{ status: "OPEN" }}
                value={form.deal_id}
                selectedLabel={form.deal_label}
                getLabel={(row) => `${row.name}${row.account?.name ? ` · ${row.account.name}` : ""}`}
                placeholder="Search open deals"
                onChange={(id, row) => setForm({ ...form, deal_id: id, deal_label: row?.name || "" })}
              />
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="space-y-1.5">
              <Label>Product</Label>
              <SearchSelect
                endpoint="/api/Product/List"
                extraParams={{ active: "true" }}
                value={form.product_id}
                selectedLabel={form.product_label}
                getLabel={(row) => `${row.name} (${row.code})`}
                placeholder="Search products"
                allowEmpty
                emptyLabel="-- Not in the catalog --"
                onChange={(id, row) => {
                  setForm({ ...form, product_id: id, product_label: row?.name || "", product_name: row?.name || form.product_name, variant_id: "" });
                  loadVariants(id);
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Shade / variant</Label>
              <select value={form.variant_id} onChange={(e) => setForm({ ...form, variant_id: e.target.value })} className={field} disabled={!form.product_id || variants.length === 0}>
                <option value="">{form.product_id && variants.length === 0 ? "No shades on this product" : "-- Any / not specific --"}</option>
                {variants.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name} ({v.code})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="space-y-1.5">
              <Label>Article name {!form.product_id && "*"}</Label>
              <Input value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} placeholder="e.g. 40s cotton poplin, navy" />
            </div>
            <div className="space-y-1.5">
              <Label>Sample type *</Label>
              <select value={form.sample_type} onChange={(e) => setForm({ ...form, sample_type: e.target.value })} className={field}>
                <option value="">-- Choose --</option>
                {types.map((t) => (
                  <option key={t.code} value={t.code}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Quantity</Label>
              <div className="flex gap-2">
                <Input type="number" min={0} step="any" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
                <select value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="h-10 w-28 shrink-0 rounded-lg border border-input bg-background px-2 text-sm">
                  <option value="">Unit</option>
                  {units.map((u) => (
                    <option key={u.code} value={u.code}>
                      {u.code}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Needed by</Label>
              <Input type="date" value={form.needed_by} onChange={(e) => setForm({ ...form, needed_by: e.target.value })} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Details</Label>
            <Textarea rows={3} value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} placeholder="Shade reference, finish, what to change from the last round..." />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? <Loader2 className="size-4 animate-spin mr-1.5" /> : null}
              {editing ? "Save changes" : "Request sample"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
